// External
import { Component, OnInit, Output, Input, EventEmitter, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Internal
import { ConfigService, DeployType } from '../../config/config.service';
import { NotificationsService } from '../../services/process/notifications/notifications.service';
import { UsefulService } from '../../services/global/useful/useful.service';
import { MessageService } from '../../services/core/message.service';
import { Owner, ProfilesService } from '../../services/process/profiles/profiles.service';
import { ValuesService } from '../../values/values.service';
import { ProductsConfigService } from '../../config/products.config.service';
import { UiIcon } from '../ui/ui-icon/ui-icon.models';
import { OffersService } from '../../services/process/offers/offers.service';
import { Router } from '@angular/router';
import { QueryParamsService } from '../../services/core/query.params.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { HandleUnrecoverableStateService } from '../../services/global/handle-unrecoverable-state/handle-unrecoverable-state.service';
import { BrowserBooleanValues, DrawerModes, Drawers, VersionTags } from '../../models/Core.model';
import { getPwaActive } from '../../../../app/app.module';
import { ServiceWorkerHelperService } from '../../services/global/service-worker-helper/service-worker-helper.service';
import { ContextBubbleLocalStorage, GroupTypes } from '../../models/subscriptions/Groups.model';
import { ContextInterface, IContextService } from '../../services/interfaces/icontext/icontext.service';
import { ISubscriptionsService } from '../../services/interfaces/isubscriptions/isubscriptions.service';
import { SubscriptionsService } from '../../services/process/subscriptions/subscriptions.service';

interface BubbleText {
    firstDescription: string;
    secondDescription: string;
}

type BubbleTexts = {
    [key in GroupTypes]?: BubbleText;
};

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {

    @Input() sidenavToggle: boolean;
    @Output() toggleDrawerNotif: EventEmitter<string> = new EventEmitter();
    @Output() toggleDrawerUser: EventEmitter<string> = new EventEmitter();
    @Output() toggleNavMenu: EventEmitter<string> = new EventEmitter();
    @Output() userDetails: EventEmitter<{}> = new EventEmitter();

    private readonly bubbleTexts: BubbleTexts = {
        [GroupTypes.VSB]: {
            firstDescription: 'group.context.created',
            secondDescription: 'group.context.switch.here'
        },
        [GroupTypes.ATO]: {
            firstDescription: 'group.context.ato.created',
            secondDescription: 'group.context.switch.here'
        }
    };
    public bubbleText: BubbleText = null;
    public contextInterface = null as ContextInterface;
    public noGroups = 0;
    notificationsCount = 0;
    public readonly owner = {} as Owner;
    public showContextPopover = false;

    public readonly getPwaActiveFunction = getPwaActive;
    public currentLocationIsOffers = false;
    private readonly onDestroy$: Subject<void> = new Subject<void>();
    public localUserDetailsNavStatus = false;
    public showPaymentMethodUpdateBanner = null;

    public burgerIcon: UiIcon.Svg = {
        type: UiIcon.Type.MENU,
        value: 'burger_menu',
        size: 20
    };

    private versionTags: VersionTags = {
        [DeployType.ALPHA]: {
            label: 'alpha',
            color: 'a2-10',
            tooltip: {
                textKey: 'global.alpha.version.warning',
                placeholders: {
                    manufacturerName: this.productsConfigService.getManufacturerName(),
                    siteName: this.configService.config.siteName
                }
            }
        },
        [DeployType.BETA]: {
            label: 'beta',
            color: 'warning-15',
            tooltip: {
                textKey: 'global.beta.version.warning',
                placeholders: {
                    manufacturerName: this.productsConfigService.getManufacturerName(),
                    siteName: this.configService.config.siteName
                }
            }
        }
    };
    public appVersion = this.versionTags[this.usefulService.getLogo().versionLabel] ?? null;

    constructor(
        public readonly usefulService                  : UsefulService,
        public readonly configService                  : ConfigService,
        private readonly notificationsService          : NotificationsService,
        private readonly messageService                : MessageService,
        private readonly profilesService               : ProfilesService,
        public readonly valuesService                  : ValuesService,
        public readonly productsConfigService          : ProductsConfigService,
        public readonly offersService                  : OffersService,
        private readonly router                        : Router,
        private readonly queryParamService             : QueryParamsService,
        public readonly deviceDetectorService          : DeviceDetectorService,
        public readonly handleUnrecoverableStateService: HandleUnrecoverableStateService,
        public readonly serviceWorkerHelperService     : ServiceWorkerHelperService,
        private readonly icontextService               : IContextService,
        private readonly iSubscriptionService          : ISubscriptionsService,
        private readonly subscriptionsService          : SubscriptionsService
    ) { }

    //*
    //*  Life-Cycle Hooks
    //*
    //#region
    ngOnInit() {
        this.messageService.getMessage()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(message => {
            switch(message.text) {
                case this.valuesService.events.dataResolverDone:
                    this.currentLocationIsOffers = window.location.pathname === this.valuesService.centralPaths.offers.path;
                    this.showPaymentMethodUpdateBanner = this.subscriptionsService.showPaymentMethodUpdateBanner();
                case this.valuesService.events.ownerChange:
                    this.getUserDetails();
                    break;
                case this.valuesService.events.notificationsCounterChange:
                    this.getNotificationsCounter();
                    break;
                case this.valuesService.events.drawer:
                    if (message?.options?.target === Drawers.USER_DETAILS || message?.options?.target === Drawers.ALL) {
                        this.localUserDetailsNavStatus = message?.options?.opened;
                    } else if (message?.options?.opened && message?.options?.mode !== DrawerModes.OVER) {
                        this.localUserDetailsNavStatus = false;
                    }
                    break;
                default:
                    break;
            }
        });

        this.messageService.getDynamicSubject(this.valuesService.events.ownerChange)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
            this.getUserDetails();
        });

        this.messageService.getDynamicSubject(this.valuesService.events.group.updateGroup)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
            this.icontextService.createContextsInterface();
            this.computeContext();
        });

        this.showPaymentMethodUpdateBanner = this.subscriptionsService.showPaymentMethodUpdateBanner();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
    //#endregion

    /**
    * Gets account owner info and sets them on local object
    * @private
    * @memberof HeaderComponent
    */
    private getUserDetails(): void {
        const owner = this.profilesService.getOwner();
        const firstName = this.profilesService.getOwnerFirstName();
        const email = this.usefulService.sanitizeValues(owner?.email);
        const profilePic = owner?.profile_pic;

        if (owner) {
            this.owner.firstname = firstName ? firstName : email;
            this.owner.email = email;
            this.owner.profile_pic = profilePic;
        }
    }

    /**
     * Computes if the context bubble should be shown
     * @returns {boolean} True if the context bubble should be shown
     */
    private computeShowContextBubble(): void {
        this.showContextPopover = localStorage.getItem(ContextBubbleLocalStorage) === BrowserBooleanValues.TRUE && !!this.profilesService.getGroups().length;
        this.bubbleText = this.bubbleTexts?.[this.profilesService.getCurrentGroupType()];
    }

    /**
     * Computes context related information. It ignores family groups.
     * @private
     * @memberof HeaderComponent
     */
    private computeContext(): void {
        this.computeShowContextBubble();
        this.contextInterface = this.icontextService.getCurrentContextInterface();
        this.noGroups = this.profilesService.getNonFamilyGroups().length;
    }

    getNotificationsCounter() {
        this.notificationsCount = this.notificationsService.getNotificationsCounter();
    }

    /**
    * This toggles left sidenav menu
    * @param {none}
    * @returns {nothing} Toggles left sidenav menu
    */
    toggleAppSideNav() {
        this.toggleNavMenu.emit(Drawers.SIDENAV);
    }

    /**
    * This toggles right sidenav user menu
    * @param {none}
    * @returns {nothing} Toggles right sidenav user menu
    */
    toggleAppDrawerUser() {
        this.toggleDrawerUser.emit(Drawers.USER_DETAILS);
        localStorage.setItem(ContextBubbleLocalStorage, BrowserBooleanValues.FALSE);
        this.showContextPopover = false;
    }

    /**
    * This toggles right sidenav notifications menu
    * @param {none}
    * @returns {nothing} Toggles right sidenav notifications menu
    */
    toggleAppDrawerNotif() {
        this.toggleDrawerNotif.emit(Drawers.NOTIFICATIONS);
    }

    /**
     * Redirect user to offers page
     * @public
     * @param {none}
     * @returns {nothing}
     */
    public goToOffers(icid?): void {
        if (icid) {
            this.queryParamService.saveQueryParamsNoPersistance({ icid: icid });
        }
        this.router.navigate([ this.valuesService.centralPaths.offers.path]);
    }

    /**
     * Shows the offline banner.
     * @returns {boolean} true If there are connectivity problems
     */
    public connectivityProblems(): boolean {
        return this.handleUnrecoverableStateService.thereAreConnectivityProblems();
    }

    /**
     * Function that opens payment details modal if all required billing fields are completed.
     * Otherwise, it opens the billing details modal.
     * @public
     * @memberof HeaderComponent
     */
    public openPaymentModal(): void {
        this.router.navigate([this.valuesService.centralPaths.subscriptions.path.concat(this.valuesService.centralPaths.subscriptions.billing.path)])
        .then(() => {
            this.iSubscriptionService.editPaymentDetails();
        });
    }
}
